import {
  ExpandCircleDown,
  ExpandMore,
  InfoOutlined,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  AppBar,
  Avatar,
  Box,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { Loader, pluralize } from '@prismamedia/one-components';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { FC } from 'react';
import { useStyles } from './styles';
import { DEFAULT_STATUSES, getHistoryInfos, StatusesType } from './utils';

export interface History {
  id: string;
  createdAt: string;
  updatedAt?: string | null;
  user?: { id: string; name?: string | null; avatarUrl?: string | null } | null;
  status?: string | null;
  data?: any;
}

interface HistoryDialogProps {
  histories: History[];
  historyCount?: number;
  loading?: boolean;
  title?: string;
  historyLabel?: string;
  createLabel?: string;
  historyInfosFn?: (
    history: History | any,
    nextHistory?: History | any,
  ) => { label?: string; value?: string };
  statuses?: StatusesType[];
}

export const HistoryDialog: FC<HistoryDialogProps> = ({
  histories,
  historyCount,
  loading,
  title = 'Historique des modifications',
  historyLabel = 'sur cet article',
  createLabel = "Création de l'article",
  historyInfosFn = getHistoryInfos,
  statuses = DEFAULT_STATUSES,
}) => {
  const classes = useStyles();

  return (
    <>
      <AppBar
        className={classes.wrapperTitle}
        color="inherit"
        position="static"
      >
        <Toolbar>
          <Typography variant="h6">{title}</Typography>
        </Toolbar>
      </AppBar>
      <DialogContent className={classes.wrapperContent}>
        <Box className={classes.wrapperInnerContent}>
          {loading ? (
            <Loader />
          ) : (
            <>
              <Box className={classes.wrapperHead}>
                <InfoOutlined color="primary" />
                <Typography>
                  {historyCount} {pluralize('modification', historyCount)}{' '}
                  {pluralize('réalisée', historyCount)} {historyLabel}.
                </Typography>
              </Box>
              {histories.map((history: History, index: number) => {
                const { label, value } = historyInfosFn(
                  history,
                  histories[index + 1],
                  createLabel,
                  statuses,
                );
                const canOpenAccordion = Boolean(label && value);
                return (
                  <Accordion
                    classes={{ root: classes.accordion }}
                    key={history.id}
                  >
                    <AccordionSummary
                      expandIcon={
                        <Tooltip
                          enterDelay={350}
                          placement="top"
                          title="Détails"
                        >
                          <ExpandMore />
                        </Tooltip>
                      }
                      classes={{ content: classes.accordionRow }}
                      {...(!canOpenAccordion && {
                        className: classes.accordionDisabled,
                      })}
                    >
                      {history.user && (
                        <Box>
                          <Avatar
                            sx={{ width: 24, height: 24 }}
                            src={history.user.avatarUrl || undefined}
                          />
                          <Typography>
                            {history.user.name || history.user.id}
                          </Typography>
                        </Box>
                      )}
                      {!!history.updatedAt && (
                        <Typography>
                          {format(
                            new Date(history.updatedAt),
                            'dd/MM/yyyy à HH:mm',
                            { locale: fr },
                          )}
                        </Typography>
                      )}
                    </AccordionSummary>

                    <AccordionDetails
                      classes={{ root: classes.accordionDetails }}
                    >
                      <TableContainer>
                        <Table>
                          <TableBody>
                            <TableRow>
                              <TableCell component="th" scope="row">
                                <ExpandCircleDown
                                  color="primary"
                                  fontSize="small"
                                />
                                Type de modification
                              </TableCell>

                              <TableCell
                                align="right"
                                component="th"
                                scope="row"
                              >
                                {label}
                              </TableCell>
                            </TableRow>

                            {value && (
                              <TableRow>
                                <TableCell component="th" scope="row">
                                  <ExpandCircleDown
                                    color="primary"
                                    fontSize="small"
                                  />
                                  Nouvelle valeur
                                </TableCell>

                                <TableCell
                                  align="right"
                                  component="th"
                                  scope="row"
                                >
                                  {value}
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </>
          )}
        </Box>
      </DialogContent>
    </>
  );
};
